// hooks/useEventActions.js
import { toast } from 'react-toastify';
import axiosClient from '../api/axiosClient';
import { useEventContext } from '../context/EventContext';

export const useEventActions = (eventId) => {
  // Usamos updateEventDetails que es la función definida en el contexto
  const { eventDetails, updateEventDetails } = useEventContext();

  // Función para actualizar el registro de asistentes en el contexto
  const updateAttendees = (attendeeId, status, additionalFields = {}) => {
    const updatedAttendees = eventDetails.attendees.map((attendee) =>
      attendee._id === attendeeId ? { ...attendee, status, ...additionalFields } : attendee
    );
    updateEventDetails({ ...eventDetails, attendees: updatedAttendees });
  };

  const handleApprove = async (attendeeId) => {
    try {
      const response = await axiosClient.post(`/api/events/${eventId}/attendees/${attendeeId}/approve`);
      if (response.data.success) {
        toast.success(response.data.message);
        if (response.data.event) {
          updateEventDetails(response.data.event);
        } else {
          updateAttendees(attendeeId, 'attending');
        }
      }
    } catch (error) {
      console.error('[handleApprove] Error en aprobación:', error);
      toast.error('Error al aprobar la inscripción.');
    }
  };

  const handleReject = async (attendeeId, organizerMessage = '') => {
    try {
      const response = await axiosClient.post(`/api/events/${eventId}/attendees/${attendeeId}/reject`, {
        organizerMessage
      });
      if (response.data.success) {
        toast.success(response.data.message);
        if (response.data.event) {
          updateEventDetails(response.data.event);
        } else {
          updateAttendees(attendeeId, 'rejected_by_organizer', { organizerMessage });
        }
      }
    } catch (error) {
      console.error('[handleReject] Error en rechazo:', error);
      toast.error('Error al rechazar la inscripción.');
    }
  };

  const handleCancelByOrganizer = async (attendeeId, refundAmount = null, organizerMessage = '') => {
    try {
      const response = await axiosClient.post(`/api/events/${eventId}/attendees/${attendeeId}/cancel-by-organizer`, {
        refundAmount,
        organizerMessage
      });
      if (response.data.success) {
        toast.success(response.data.message);
        if (response.data.event) {
          updateEventDetails(response.data.event);
        } else {
          updateAttendees(attendeeId, 'cancelled_by_organizer', { organizerMessage });
        }
      }
    } catch (error) {
      console.error('[handleCancelByOrganizer] Error en cancelación por organizador:', error);
      toast.error('Error al cancelar la inscripción.');
    }
  };

  const handleDenyCancellation = async (attendeeId, organizerMessage = '') => {
    try {
      const response = await axiosClient.post(`/api/events/${eventId}/attendees/${attendeeId}/deny-cancellation`, {
        organizerMessage
      });
      if (response.data.success) {
        toast.success(response.data.message);
        if (response.data.event) {
          updateEventDetails(response.data.event);
        } else {
          updateAttendees(attendeeId, 'attending', { organizerMessage });
        }
      }
    } catch (error) {
      console.error('[handleDenyCancellation] Error en denegación de cancelación:', error);
      toast.error('Error al denegar la cancelación.');
    }
  };

  // Función para aceptar cancelaciones (tickets pagados)
  const handleAcceptCancellation = async (attendeeId, refundAmountCents = null, organizerMessage = '') => {
    try {
      const response = await axiosClient.post(`/api/events/${eventId}/attendees/${attendeeId}/accept-cancellation`, {
        refundAmountCents,
        organizerMessage
      });
      if (response.data.success) {
        toast.success(response.data.message);
        if (response.data.event) {
          updateEventDetails(response.data.event);
        } else {
          updateAttendees(
            attendeeId,
            response.data.attendee?.status || 'refund_accepted',
            { organizerMessage }
          );
        }
      }
    } catch (error) {
      console.error('[handleAcceptCancellation] Error en aceptación de cancelación:', error);
      toast.error('Error al aceptar la cancelación.');
    }
  };

  // Función para marcar como reembolsado
  const handleMarkRefunded = async (attendeeId, organizerMessage = '') => {
    try {
      const response = await axiosClient.post(`/api/events/${eventId}/attendees/${attendeeId}/mark-refunded`, {
        organizerMessage
      });
      if (response.data.success) {
        toast.success(response.data.message);
        if (response.data.event) {
          updateEventDetails(response.data.event);
        } else {
          updateAttendees(attendeeId, 'refunded', { organizerMessage });
        }
      }
    } catch (error) {
      console.error('[handleMarkRefunded] Error al marcar como reembolsado:', error);
      toast.error('Error al marcar como reembolsado.');
    }
  };



  return {
    handleApprove,
    handleReject,
    handleCancelByOrganizer,
    handleDenyCancellation,
    handleAcceptCancellation,
    handleMarkRefunded
  };
};
